"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { AnimatePresence, m, useReducedMotion } from "framer-motion";
import { useIsDark } from "@/context/ThemeContext";
import useAutoplayVisibility from "@/hooks/useAutoplayVisibility";

export default function ExcellenceStack({
  items,
  className = "",
  autoRotate = true,
  intervalMs = 2200,
  initialIndex = 0,
}) {
  const isDark = useIsDark();
  const [active, setActive] = useState(initialIndex % items.length);
  const timerRef = useRef(null);
  const rootRef = useRef(null);

  const prefersReduced = useReducedMotion();
  const start = () => {
    if (!autoRotate || prefersReduced) return;
    clear();
    timerRef.current = setInterval(
      () => {
        setActive((i) => (i + 1) % items.length);
      },
      Math.max(intervalMs, 2800)
    );
  };
  const clear = () => timerRef.current && clearInterval(timerRef.current);

  useEffect(() => {
    start();
    return clear;
  }, [autoRotate, intervalMs, items.length]);

  useAutoplayVisibility({ ref: rootRef, start, clear, threshold: 0.15 });
  useEffect(() => () => clear(), []);

  return (
    <section
      ref={rootRef}
      className={`relative w-full xl:max-w-[760px] ${className} touch-pan-y`}
      onMouseEnter={clear}
      onMouseLeave={start}
      onFocusCapture={clear}
      onBlurCapture={start}
      aria-label="Excellence rotating features"
    >
      <div className="flex flex-col gap-4 w-full">
        {items.map((it, i) => {
          const isActive = i === active;

          return (
            <m.div
              key={it.id}
              layout
              onClick={() => setActive(i)}
              className="relative w-full  overflow-hidden rounded-[20px] dark:bg-[#121212] bg-[#FFFFFF] cursor-pointer select-none"
              style={{
                height: isActive ? 324 : 100,
                willChange: "transform, opacity",
                transform: "translateZ(0)",
              }}
              transition={{ type: "spring", stiffness: 220, damping: 26 }}
              role="button"
              aria-pressed={isActive}
            >
              {/* Expanded background image */}
              <AnimatePresence initial={false}>
                {isActive && (
                  <m.div
                    key="bg"
                    className="absolute inset-0"
                    initial={{ opacity: 0.0, scale: 1.02 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.01 }}
                    transition={{ duration: 0.35 }}
                    style={{
                      willChange: "opacity, transform",
                      transform: "translateZ(0)",
                    }}
                  >
                    <Image
                      src={isDark ? it.darkBg : it.lightBg}
                      alt={it.id}
                      aria-hidden="true"
                      fill
                      sizes="(max-width: 480px) 100vw, (max-width: 768px) 90vw,(max-width: 1200px) 60vw, (max-width: 1700px) 40vw, 800px"
                      quality={i === active ? 90 : 65}
                      placeholder={
                        typeof (isDark ? it.darkBg : it.lightBg) === "string"
                          ? undefined
                          : "blur"
                      }
                      priority={i === active}
                      loading={i === active ? "eager" : "lazy"}
                      fetchPriority={i === active ? "high" : "auto"}
                      decoding={i === active ? "sync" : "async"}
                      className="object-cover"
                      style={{
                        willChange:
                          i === active ? "opacity, transform" : "auto",
                      }}
                    />
                  </m.div>
                )}
              </AnimatePresence>

              {/* Title */}
              <m.h3
                layout
                className={`absolute left-4 top-4 3xl:top-8 4xl:top-10 font-manrope font-bold tracking-[0.02em] bg-gradient-to-r  bg-clip-text text-transparent
                 ${
                   isActive
                     ? "text-[19px] mobile:text-xl sm:text-2xl md:text-3xl tablet:text-[32px] lg:text-[29px] xl:text-[32px] laptop:text-[38px] 3xl:text-[42px] 4xl:text-[46px] leading-snug bg-[linear-gradient(94.97deg,var(--pinks)_20.31%,var(--secondary)_110.92%)]"
                     : "text-[12px] mobile:text-[20px] sm:text-[22px] lg:text-[24px] xl:text-[24px] laptop:text-[25px] 3xl:text-[20px] 4xl:text-[22px] bg-[linear-gradient(94.97deg,var(--pinks)_56.31%,var(--secondary)_120.92%)]"
                 }`}
                transition={{ type: "spring", stiffness: 220, damping: 26 }}
              >
                {it.title}
              </m.h3>

              {/* Body */}
              {isActive && (
                <m.p
                  className="absolute left-4 bottom-[8%] mobile:bottom-[15%] lg:bottom-[8%] xl:bottom-[10%] laptop:bottom-[6%] 3xl:bottom-[12%] 4xl:bottom-[13%] w-[90%] font-manrope font-medium text-sm mobile:text-base  sm:text-lg md:text-md lg:text-lg xl:text-[18px] laptop:text-[19px] 3xl:text-[24px] 4xl:text-[26px] leading-relaxed tracking-[0.02em] dark:text-[#FFFFFF] text-[#000000]"
                  initial={false}
                  animate={{
                    opacity: isActive ? 1 : 0,
                    scaleY: isActive ? 1 : 0.9,
                    y: isActive ? 0 : 4,
                  }}
                  transition={{
                    duration: 0.25,
                    ease: "easeOut",
                  }}
                >
                  {it.body}
                </m.p>
              )}
            </m.div>
          );
        })}
      </div>
    </section>
  );
}
